import { getApiBaseUrl } from "src/config/Endpoints";
import { normalizeApiLang, type ApiLang } from "src/utils/apiLang";

const API_BASE_URL = getApiBaseUrl();

export const submitBasicInfo = async ({
  tenant_id,
  user_id,
  tenant_name,
  first_name,
  last_name,
  phone_number,
  website_url,
  api_key,
  industry,
  profile_image, // add this prop
}: {
  tenant_id: string | null;
  user_id: string | null;
  tenant_name: string;
  first_name: string;
  last_name: string;
  phone_number: string | undefined;
  website_url: string;
  api_key?: string | null;
  industry: string | null;
  profile_image?: File | null; // make it optional
}) => {
  try {
    if (!api_key) {
      return { ok: false, data: { message: "Missing API key" } };
    }

    const formData = new FormData();
    formData.append("tenant_id", tenant_id || "");
    formData.append("user_id", user_id || "");
    formData.append("tenant_name", tenant_name);
    formData.append("first_name", first_name);
    formData.append("last_name", last_name);
    formData.append("phone_number", phone_number || "");
    formData.append("website_url", website_url);
    formData.append("industry", industry || "");
    if (profile_image) {
      formData.append("profile_image", profile_image);
    }

    // Authorization works with FormData when Content-Type is left unset so the
    // browser can attach multipart/form-data with the correct boundary.
    const response = await fetch(
      `${API_BASE_URL}/tenant-basic-info`,
      {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${api_key}`,
        },
      }
    );

    const result = await response.json();
    return { ok: response.ok, data: result };
  } catch (error) {
    console.error("Error submitting basic info:", error);
    return { ok: false, data: { message: "Network error" } };
  }
};
/*******Fetch Dashboards for User*******/

export interface Dashboard {
  dashboard_id?: number;
  dashboard_name: string;
  dashboard_url?: string;
}

export class DashboardApiError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = "DashboardApiError";
    this.code = code;
  }
}

export const fetchAllDashboardsAPI = async (
  user_id: string,
  api_key: string,
  lang?: string | null | ApiLang
): Promise<Dashboard[]> => {
  const query = new URLSearchParams({ lang: normalizeApiLang(lang) });
  const response = await fetch(
    `${API_BASE_URL}/users/dashboards-url?${query}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${api_key}`,
      },
      body: JSON.stringify({ user_id }),
    },
  );

  if (!response.ok) {
    let errorMessage = `Dashboard fetch failed with status ${response.status}`;
    let errorCode: string | undefined;

    try {
      const errorPayload = await response.json();
      if (typeof errorPayload?.message === "string" && errorPayload.message.trim()) {
        errorMessage = errorPayload.message;
      }
      if (typeof errorPayload?.code === "string" && errorPayload.code.trim()) {
        errorCode = errorPayload.code;
      }
    } catch {
      // Ignore non-JSON error payloads and keep the fallback message.
    }

    throw new DashboardApiError(errorMessage, errorCode);
  }

  return await response.json();
};

export const slugify = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, "-");
};
